import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import { AudioDelay, ControllerType, Student } from '@/common/types/student';
import { StudentEventType } from '@/common/types/studentEvents';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  generateStudentPassword,
  getNewDailyActivities,
  isValidUsername,
  sanitizeUsername,
} from '@/common/utils/student';
import { getSettingDisplayValues } from '@/common/utils/studentEvents';
import api from '@/db/apiClient';
import {
  CheckCircleIcon,
  CircleNotchIcon,
  EyeIcon,
  EyeSlashIcon,
  IdentificationCardIcon,
  KeyIcon,
  MagicWandIcon,
  MagnifyingGlassIcon,
  ShuffleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@shadcn/components/accordion';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shadcn/components/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';
import LearnerSettings from './LearnerSettings';

interface LearnerDetailsProps {
  student: Student;
}
export default function LearnerDetails({ student }: LearnerDetailsProps) {
  const studentId = student.id;

  const { user } = useUser();
  const { currentOrganisationId: orgId } = useCurrentOrganisationId();

  const [updateStudent] = useUpdateStudentMutation();

  const [updatedFirstName, setUpdatedFirstName] = useState(
    student.studentFirstName,
  );
  const [updatedSurname, setUpdatedSurname] = useState(student.studentSurname);
  const [updatedUsername, setUpdatedUsername] = useState(
    student.spellcastUsername,
  );
  const [usernameStatus, setUsernameStatus] = useState<boolean | undefined>();
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [updatedPassword, setUpdatedPassword] = useState(student.password);
  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    setUpdatedFirstName(student.studentFirstName);
    setUpdatedSurname(student.studentSurname);
    setUpdatedUsername(student.spellcastUsername);
    setUpdatedPassword(student.password);
  }, [student]);

  const checkUsernameAvailability = useMemo(
    () =>
      debounce(async (username: string) => {
        if (!username || !isValidUsername(username)) {
          setUsernameStatus(false);
          return;
        }

        if (username === student.spellcastUsername) {
          setUsernameStatus(undefined);
          return;
        }

        setIsCheckingUsername(true);
        const isAvailable = await api.asEducator.isUsernameAvailable(username);
        setUsernameStatus(isAvailable);
        setIsCheckingUsername(false);
      }, 500),
    [student.spellcastUsername],
  );

  async function handleSave(passwordOverride?: string) {
    const password = passwordOverride ?? updatedPassword;

    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner details', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    if (
      updatedUsername === student.spellcastUsername &&
      updatedFirstName === student.studentFirstName &&
      updatedSurname === student.studentSurname &&
      password === student.password
    ) {
      return;
    }

    if (
      updatedUsername !== student.spellcastUsername &&
      usernameStatus !== true
    ) {
      setUpdatedUsername(student.spellcastUsername);
      return;
    }

    if (!updatedUsername || !updatedFirstName || !updatedSurname || !password) {
      setUpdatedUsername(student.spellcastUsername);
      setUpdatedFirstName(student.studentFirstName);
      setUpdatedSurname(student.studentSurname);
      setUpdatedPassword(student.password);
      return;
    }

    trackMixpanelEvent('Learner Details Changed', {
      studentId: student.id,
      previous: {
        firstName: student.studentFirstName,
        surname: student.studentSurname,
        username: student.spellcastUsername,
        password: student.password,
      },
      updated: {
        firstName: updatedFirstName,
        surname: updatedSurname,
        username: updatedUsername,
        password,
      },
    });

    await updateStudent({
      orgId,
      studentId,
      updates: {
        studentFirstName: updatedFirstName,
        studentSurname: updatedSurname,
        spellcastUsername: updatedUsername,
        password,
      },
    });

    toast.success('Learner details updated');
  }

  async function addSettingChangedEvent(
    setting: string,
    oldValue: string | number | boolean,
    newValue: string | number | boolean,
  ) {
    await api.asEducator.addStudentEvent({
      type: StudentEventType.SETTING_CHANGED,
      organisationId: student.organisationId,
      studentId: student.id,
      createdAt: Date.now(),
      actor: { type: 'educator', name: user?.name ?? 'Educator' },
      nestedUnderSessionId: null,
      changes: [getSettingDisplayValues(setting, oldValue, newValue)],
      location: 'learner-details',
    });
  }

  async function handlePlayPhonemeChanged(playPhonemeOnCardDrop: boolean) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'playPhonemeOnCardDrop',
      previous: student.settings.playPhonemeOnCardDrop,
      updated: playPhonemeOnCardDrop,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          playPhonemeOnCardDrop,
        },
      },
    });
    addSettingChangedEvent(
      'playPhonemeOnCardDrop',
      student.settings.playPhonemeOnCardDrop,
      playPhonemeOnCardDrop,
    );
    toast.success('Learner settings updated');
  }

  async function handleControllerTypeChange(updated: ControllerType) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'controllerType',
      previous: student.settings.controllerType,
      updated,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          controllerType: updated,
        },
      },
    });
    addSettingChangedEvent(
      'controllerType',
      student.settings.controllerType,
      updated,
    );
    toast.success('Learner settings updated');
  }

  async function handleDuelsEnabledChange(duelsEnabled: boolean) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    const newSettings = { ...student.settings, duelsEnabled };
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'duelsEnabled',
      previous: student.settings.duelsEnabled,
      updated: duelsEnabled,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: newSettings,
        dailyActivities: getNewDailyActivities(newSettings),
      },
    });
    addSettingChangedEvent(
      'duelsEnabled',
      student.settings.duelsEnabled,
      duelsEnabled,
    );
    toast.success('Learner settings updated');
  }

  async function handleAudioDelayChange(audioDelay: number) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'audioDelay',
      previous: student.settings.audio.audioDelay,
      updated: audioDelay,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          audio: {
            ...student.settings.audio,
            audioDelay,
          },
        },
      },
    });
    addSettingChangedEvent(
      'audioDelay',
      student.settings.audio.audioDelay ?? 0,
      audioDelay,
    );
    toast.success('Learner settings updated');
  }

  async function handleDistractorCardsChange(distractorCardsEnabled: boolean) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'distractorCardsEnabled',
      previous: student.settings.distractorCardsEnabled,
      updated: distractorCardsEnabled,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          distractorCardsEnabled,
        },
      },
    });
    addSettingChangedEvent(
      'distractorCardsEnabled',
      student.settings.distractorCardsEnabled,
      distractorCardsEnabled,
    );
    toast.success('Learner settings updated');
  }

  async function handleWordLimitChange(wordLimit: number) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'wordLimit',
      previous: student.settings.wordLimit,
      updated: wordLimit,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          wordLimit,
        },
      },
    });
    addSettingChangedEvent('wordLimit', student.settings.wordLimit, wordLimit);
    toast.success('Learner settings updated');
  }

  async function handleWordSegmentationChange(
    wordSegmentationEnabled: boolean,
  ) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    // If turning off segmentation, reset audio delay to fast
    const newSettings = { ...student.settings, wordSegmentationEnabled };
    if (!wordSegmentationEnabled) {
      newSettings.audio = {
        ...newSettings.audio,
        audioDelay: AudioDelay.SHORT,
      };
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'wordSegmentationEnabled',
      previous: student.settings.wordSegmentationEnabled,
      updated: wordSegmentationEnabled,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: newSettings,
      },
    });
    addSettingChangedEvent(
      'wordSegmentationEnabled',
      student.settings.wordSegmentationEnabled,
      wordSegmentationEnabled,
    );
    toast.success('Learner settings updated');
  }

  async function handleArticulatoryGesturesChange(
    articulatoryGesturesEnabled: boolean,
  ) {
    if (!orgId) {
      console.error('Missing org ID');
      toast.error('Failed to update learner settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    trackMixpanelEvent('Learner Settings Changed', {
      studentId: student.id,
      setting: 'articulatoryGesturesEnabled',
      previous: student.settings.articulatoryGesturesEnabled,
      updated: articulatoryGesturesEnabled,
    });
    await updateStudent({
      orgId,
      studentId,
      updates: {
        settings: {
          ...student.settings,
          articulatoryGesturesEnabled,
        },
      },
    });
    addSettingChangedEvent(
      'articulatoryGesturesEnabled',
      student.settings.articulatoryGesturesEnabled,
      articulatoryGesturesEnabled,
    );
    toast.success('Learner settings updated');
  }

  return (
    <LearnerDetailsContainer>
      <Accordion type="multiple" defaultValue={['details', 'settings']}>
        <AccordionItem value="details">
          <AccordionTrigger>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Learner Details
          </AccordionTrigger>

          <AccordionContent className="grid gap-3">
            <LearnerDetailsRow>
              <LearnerDetailLabel>
                <MagnifyingGlassIcon size={16} />
                Student ID
              </LearnerDetailLabel>
              <span className="text-neutral-500 ml-2">{student.id}</span>
            </LearnerDetailsRow>

            <LearnerDetailsRow>
              <LearnerDetailLabel>
                <IdentificationCardIcon size={16} />
                Name
              </LearnerDetailLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <PopoverButton
                    value={`${student.studentFirstName} ${student.studentSurname}`}
                  />
                </PopoverTrigger>
                <PopoverContent className="pt-2">
                  <Label
                    htmlFor="firstName"
                    className="text-xs text-muted-foreground"
                  >
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    value={updatedFirstName}
                    onChange={(e) => setUpdatedFirstName(e.target.value)}
                    onBlur={() => handleSave()}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                  />

                  <Label
                    htmlFor="surname"
                    className="text-xs text-muted-foreground"
                  >
                    Surname
                  </Label>
                  <Input
                    id="surname"
                    value={updatedSurname}
                    onChange={(e) => setUpdatedSurname(e.target.value)}
                    onBlur={() => handleSave()}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                  />
                </PopoverContent>
              </Popover>
            </LearnerDetailsRow>

            <LearnerDetailsRow>
              <LearnerDetailLabel>
                <MagicWandIcon size={16} />
                Username
              </LearnerDetailLabel>
              <div className="relative">
                <Input
                  value={updatedUsername}
                  onChange={(e) => {
                    const newUsername = sanitizeUsername(e.target.value);
                    setUpdatedUsername(newUsername);
                    checkUsernameAvailability(newUsername);
                  }}
                  onBlur={() => handleSave()}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center">
                        {isCheckingUsername ? (
                          <CircleNotchIcon
                            size={16}
                            className="text-muted-foreground animate-spin"
                          />
                        ) : updatedUsername === student.spellcastUsername ? (
                          <CheckCircleIcon
                            size={16}
                            className="text-green-500"
                          />
                        ) : updatedUsername === '' ? (
                          <WarningCircleIcon
                            size={16}
                            className="text-red-500"
                          />
                        ) : usernameStatus ? (
                          <CheckCircleIcon
                            size={16}
                            className="text-green-500"
                          />
                        ) : (
                          <WarningCircleIcon
                            size={16}
                            className="text-red-500"
                            weight="fill"
                          />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isCheckingUsername
                        ? 'Checking username...'
                        : updatedUsername === student.spellcastUsername
                          ? 'Current username'
                          : updatedUsername === ''
                            ? 'Username required'
                            : !isValidUsername(updatedUsername)
                              ? 'Invalid username'
                              : usernameStatus
                                ? 'Username available'
                                : 'Username unavailable'}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </LearnerDetailsRow>

            <LearnerDetailsRow>
              <LearnerDetailLabel>
                <KeyIcon size={16} />
                Password
              </LearnerDetailLabel>
              <div className="relative">
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  value={updatedPassword}
                  onChange={(e) => {
                    setUpdatedPassword(e.target.value);
                  }}
                  onBlur={() => {
                    handleSave();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
                <div className="flex items-center gap-1 absolute right-1 top-1/2 -translate-y-1/2 bg-white">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          const newPassword = generateStudentPassword();
                          setUpdatedPassword(newPassword);
                          handleSave(newPassword);
                        }}
                      >
                        <ShuffleIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Generate random password</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPasswordVisible((prev) => !prev)}
                      >
                        {passwordVisible ? (
                          <EyeSlashIcon className="size-4" />
                        ) : (
                          <EyeIcon className="size-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {passwordVisible ? 'Hide' : 'Show'} Password
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </LearnerDetailsRow>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="settings" className="border-none">
          <AccordionTrigger>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Game Settings
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-6">
            <LearnerSettings
              settings={student.settings}
              onPlayPhonemeChanged={handlePlayPhonemeChanged}
              onControllerTypeChange={handleControllerTypeChange}
              onDuelsEnabledChange={handleDuelsEnabledChange}
              onAudioDelayChange={handleAudioDelayChange}
              onDistractorCardsChange={handleDistractorCardsChange}
              onWordLimitChange={handleWordLimitChange}
              onWordSegmentationChange={handleWordSegmentationChange}
              onArticulatoryGesturesChange={handleArticulatoryGesturesChange}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </LearnerDetailsContainer>
  );
}

const LearnerDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`;

const LearnerDetailsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  margin-right: 8px;
`;

const LearnerDetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: hsl(var(--muted-foreground));
  font-size: 12px;
`;

const PopoverButton = styled(Input)`
  cursor: pointer;
  text-align: left;
  &:hover {
    background-color: hsl(var(--muted));
  }
`;
